import React from 'react';

import { CashFlowConfig } from '@ndvr/portfolio-api';
import { IncomeReport } from '@ndvr/reporting-api';

import { formatCurrency } from 'utils';
import { incompleteInflationNote, totalPolicyWithdrawals } from 'views/portfolio/policy-withdrawals';

import { EM_DASH, formatFullCurrency } from '../../reporting/format';
import { HeroStatCard } from '../../reporting/HeroStatCard';
import { buildIncomeMatrix } from '../income';

interface IncomeHeroProps {
    // Null while the household has no income projection yet (the endpoint answers 204).
    report?: IncomeReport | null;
    // The scope's planned withdrawals (getScopedWithdrawals).
    withdrawals: CashFlowConfig[];
    // The withdrawal plans failed to load, so `withdrawals` may be missing some.
    isWithdrawalsError: boolean;
    isLoading: boolean;
}

// Whole dollars with the exact figure on hover; a dash for income the report does not have.
const moneyStat = (label: string, value: number | string | undefined) =>
    value === undefined
        ? { label, value: EM_DASH, tooltip: 'No income projection for these accounts yet.' }
        : { label, value: formatCurrency(value), tooltip: formatFullCurrency(value) };

// The section's headline figures: income received over the trailing 12 months, income projected
// over the next 12 and the withdrawals planned over the next 12.
export const IncomeHero = ({ report, withdrawals, isWithdrawalsError, isLoading }: IncomeHeroProps) => {
    const projected = report ? buildIncomeMatrix(report.projectedIncome).grandTotal : undefined;
    // NaN when a withdrawal's inflation settings are incomplete, as on the Withdrawal Plan.
    const planned = totalPolicyWithdrawals(withdrawals, 1);
    const unavailable = (tooltip: string) => ({ label: 'Planned Withdrawals Next 12M', value: 'Unavailable', tooltip });

    const stats = [
        moneyStat('Trailing 12M Income', report?.previousTwelveMonths.totalIncome),
        {
            ...moneyStat('Projected Next 12M', projected),
            calculation: 'Projected income for the current quarter and the next three.',
        },
        isWithdrawalsError
            ? unavailable('Unable to load withdrawal plan.')
            : Number.isFinite(planned)
              ? moneyStat('Planned Withdrawals Next 12M', planned)
              : unavailable(incompleteInflationNote(withdrawals)),
    ];

    return <HeroStatCard stats={stats} isLoading={isLoading} />;
};
